import React, { useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import {
  Button,
  Grid,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import i18next from 'i18next';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { useDispatch } from 'react-redux';

import { CustomTabsControlled } from './Tabs';
import SimpleTable from './table/SimpleTable';
import { FormatDateTime, FormatDate } from './DateTime';
import SimpleDialog from './dialogs/Dialog';
import { enqueueSnackbar } from '../store/snackbarSlice';
import {
  WLOfferHistoryDetailServiceOutput,
  WLOfferHistoryDetailAttendeeOutput,
  WLOfferHistoryDetailPlanningOutput,
  WLOfferHistoryDetailBillingOutput,
  WLOfferHistoryListShortFragment,
  EOfferVersionHistoryStatus,
} from '__generated__/graphql';
import { userSelector } from 'helper/security';

// GraphQL Mutation for resending emails
const RESEND_EMAIL_FROM_HISTORY = gql`
  mutation ResendEmailFromHistory($emailHistoryId: Int!, $ccEmails: [String!]) {
    resendEmailFromHistory(emailHistoryId: $emailHistoryId, ccEmails: $ccEmails) {
      success
      message
    }
  }
`;

// Use the generated GraphQL fragment type
export type HistoryItem = WLOfferHistoryListShortFragment;

export interface EmailHistoryItem {
  id: number;
  createdAt: any;
  success: boolean;
  subject?: string | null;
  fromAddress?: string | null;
  replyToAddress?: string | null;
  templateName?: string | null;
  templateVariables?: string | null;
  language?: string | null;
  body?: string | null;
  err?: string | null;
  userName?: string | null;
  sentAt?: any;

  // Enhanced recipient information - matches GraphQL generated types
  recipients?:
  | {
    id: number;
    emailAddress: string;
    recipientType: string;
    source: string;
    sourceName?: string | null;
    hotelContactId?: number | null;
    clientId?: number | null;
    userId?: number | null;
    deliveryStatus: string;
    deliveryError?: string | null;
    deliveryStatusAt?: any;
  }[]
  | null
  | undefined;

  // Attachment information - matches GraphQL generated types
  attachments?:
  | {
    id: number;
    filename: string;
    attachmentType: string;
    offerVersionId?: number | null;
    eventId?: number | null;
    mimeType?: string | null;
  }[]
  | null
  | undefined;
}

interface OfferHistoryTableProps {
  history: HistoryItem[];
  emailHistory: EmailHistoryItem[];
  canSeeClientData: boolean;
  onOfferVersionClick?: (versionId: number) => void;
  onEmailResent?: () => void; // Callback to refresh email history after resend
}

/**
 * Table component displaying offer version history and email history.
 * Supports resending emails with CC recipients and viewing detailed history entries.
 * Groups history details by day and type (service, attendee, planning, billing).
 */
export default function OfferHistoryTable({ history, emailHistory, canSeeClientData, onOfferVersionClick, onEmailResent }: OfferHistoryTableProps) {
  const user = userSelector()!;
  const [selectedHistoryTab, setSelectedHistoryTab] = useState(0);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(-1);
  const [currentHistoryData, setCurrentHistoryData] = useState<any[]>([]);

  // Resend confirmation dialog state
  const [resendConfirmDialog, setResendConfirmDialog] = useState<{
    open: boolean;
    emailHistoryId: number | null;
    subject: string | null;
    hotelName: string | null;
    recipientInfo: string | null;
  }>({
    open: false,
    emailHistoryId: null,
    subject: null,
    hotelName: null,
    recipientInfo: null,
  });

  // CC emails state for resend modal
  const [showCcInput, setShowCcInput] = useState(false);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [newCcEmail, setNewCcEmail] = useState('');

  // Apollo mutation hook for resending emails
  const [resendEmailMutation, { loading: resendLoading }] = useMutation(RESEND_EMAIL_FROM_HISTORY);

  // Redux dispatch for enqueuing snackbar notifications
  const dispatch = useDispatch();

  // Helper function to format RESPONSIBLE_PERSON_TRANSFERRED history entries
  const formatResponsiblePersonTransferredInfo = (extraInfo: string): string => {
    try {
      const info = JSON.parse(extraInfo);
      if (info.action === 'RESPONSIBLE_PERSON_TRANSFERRED' || info.action === 'RESPONSIBLE_ACCOUNT_TRANSFERRED') {
        const parts = [];

        // Use the names directly from the stored extraInfo
        if (info.oldClientName && info.newClientName) {
          parts.push(
            String(
              i18next.t('offer-history-transfer-from-to', {
                oldClientName: info.oldClientName,
                oldCompanyName: info.oldCompanyName ? ` (${info.oldCompanyName})` : '',
                newClientName: info.newClientName,
                newCompanyName: info.newCompanyName ? ` (${info.newCompanyName})` : '',
              }),
            ),
          );
        } else if (info.newClientName) {
          parts.push(
            String(
              i18next.t('offer-history-transfer-to', {
                newClientName: info.newClientName,
                newCompanyName: info.newCompanyName ? ` (${info.newCompanyName})` : '',
              }),
            ),
          );
        } else {
          parts.push(String(i18next.t('offer-history-transfer-to-new-contact')));
        }

        if (info.reason) {
          parts.push(String(i18next.t('offer-history-transfer-reason', { reason: info.reason })));
        }

        return parts.join('. ');
      }
    } catch (error) {
      // If parsing fails, return the raw string
    }
    return extraInfo;
  };

  // Function to translate template names
  const translateTemplateName = (templateName: string | null | undefined): string => {
    if (!templateName) return '-';
    const translationKey = `enums-EContentEmailTemplate-${templateName}`;
    const translated = i18next.t(translationKey, { defaultValue: templateName });
    return translated;
  };

  // Function to translate source names
  const translateSourceName = (source: string | null | undefined): string => {
    if (!source) return i18next.t('offer-email-history-unknown');
    const translationKey = `enums-EEmailRecipientSource-${source}`;
    const translated = i18next.t(translationKey, { defaultValue: source });
    return translated;
  };

  // Function to translate delivery status values (PENDING/SUCCESS/ERROR)
  const translateDeliveryStatus = (status: string | null | undefined): string => {
    if (!status) return '-';
    return String(i18next.t(`enums-EEmailDeliveryStatus-${status}`, { defaultValue: status }));
  };

  // MUI Chip color per delivery status - ERROR is highlighted in red per requirement
  const deliveryStatusChipColor = (status: string | null | undefined): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'ERROR':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Rollup delivery status across all recipients of one email: any ERROR wins, then all-SUCCESS, otherwise PENDING
  const getRecipientsDeliveryStatus = (recipients: EmailHistoryItem['recipients']): 'ERROR' | 'PENDING' | 'SUCCESS' | null => {
    if (!recipients || recipients.length === 0) return null;
    if (recipients.some(r => r.deliveryStatus === 'ERROR')) return 'ERROR';
    if (recipients.every(r => r.deliveryStatus === 'SUCCESS')) return 'SUCCESS';
    return 'PENDING';
  };

  // Function to format recipient information with type and source
  const formatRecipientInfo = (recipients: EmailHistoryItem['recipients']): string => {
    if (!recipients || recipients.length === 0) {
      return i18next.t('offer-email-history-no-recipients');
    }

    // For display in table, keep it concise
    if (recipients.length === 1) {
      const r = recipients[0];
      const sourceName = translateSourceName(r.source);
      return `${r.recipientType}: ${r.emailAddress} (${sourceName})`;
    } else {
      // Multiple recipients - show count and first recipient
      const first = recipients[0];
      const sourceName = translateSourceName(first.source);
      return String(
        i18next.t('offer-history-email-recipients-count-preview', {
          count: recipients.length,
          recipientType: first.recipientType,
          emailAddress: first.emailAddress,
          sourceName,
          suffix: recipients.length > 1 ? '...' : '',
        }),
      );
    }
  };

  // Function to format primary recipients (Hotel/Client)
  const formatPrimaryRecipients = (recipients: EmailHistoryItem['recipients']): string => {
    if (!recipients || recipients.length === 0) {
      return i18next.t('offer-email-history-no-recipients');
    }

    const primarySources = ['HOTEL_BUSINESS_EMAIL', 'HOTEL_NOTIFICATION_EMAIL', 'CLIENT_EMAIL'];
    const primaryRecipients = recipients.filter(r => primarySources.includes(r.source));
    if (primaryRecipients.length === 0) {
      return i18next.t('offer-email-history-no-recipients');
    }

    if (primaryRecipients.length === 1) {
      const r = primaryRecipients[0];
      const sourceName = translateSourceName(r.source);
      return `${r.emailAddress} (${sourceName})`;
    } else {
      const first = primaryRecipients[0];
      const sourceName = translateSourceName(first.source);
      return `${first.emailAddress} (${sourceName})${primaryRecipients.length > 1 ? ` +${primaryRecipients.length - 1}` : ''}`;
    }
  };

  // Function to format alternative recipients (CC recipients and all non-primary sources)
  const formatAlternativeRecipients = (recipients: EmailHistoryItem['recipients']): string => {
    if (!recipients || recipients.length === 0) {
      return '-';
    }

    const primarySources = ['HOTEL_BUSINESS_EMAIL', 'HOTEL_NOTIFICATION_EMAIL', 'CLIENT_EMAIL'];
    // Include CC recipients and recipients that are not primary sources
    const altRecipients = recipients.filter(
      r => r.recipientType === 'CC' || !primarySources.includes(r.source)
    );
    if (altRecipients.length === 0) {
      return '-';
    }

    // Format CC emails (from resend) - show them as comma-separated list
    const ccRecipients = altRecipients.filter(r => r.recipientType === 'CC');
    if (ccRecipients.length > 0) {
      const ccEmails = ccRecipients.map(r => r.emailAddress).join(', ');
      return ccEmails;
    }

    // For other alternative recipients, show source info
    if (altRecipients.length === 1) {
      const r = altRecipients[0];
      const sourceName = translateSourceName(r.source);
      return `${r.emailAddress} (${sourceName})`;
    } else {
      return String(i18next.t('offer-history-email-more-recipients', { count: altRecipients.length }));
    }
  };

  // Function to get email description
  const getEmailDescription = (templateName: string | null | undefined): string => {
    if (!templateName) return '-';

    // Map template names to descriptions
    const templateDescriptions: Record<string, string> = {
      BOOKING_RECEIVED_CLIENT: String(i18next.t('offer-history-template-booking-received-client')),
      BOOKING_RECEIVED_HOTEL: String(i18next.t('offer-history-template-booking-received-hotel')),
      BOOKING_APPROVED_CLIENT: String(i18next.t('offer-history-template-booking-approved-client')),
      BOOKING_APPROVED_HOTEL: String(i18next.t('offer-history-template-booking-approved-hotel')),
      BOOKING_REJECTED_CLIENT: String(i18next.t('offer-history-template-booking-rejected-client')),
      BOOKING_REJECTED_HOTEL: String(i18next.t('offer-history-template-booking-rejected-hotel')),
      OFFER_RECEIVED_CLIENT: String(i18next.t('offer-history-template-offer-received-client')),
      OFFER_REMINDER_CLIENT: String(i18next.t('offer-history-template-offer-reminder-client')),
      OFFER_REMINDER_HOTEL: String(i18next.t('offer-history-template-offer-reminder-hotel')),
      CLIENT_VERIFICATION: String(i18next.t('offer-history-template-client-verification')),
      LISTER_NONBINDING_REQUEST_HOTEL: String(i18next.t('offer-history-template-lister-nonbinding-request-hotel')),
      LISTER_BOOKING_DIRECT_CLIENT: String(i18next.t('offer-history-template-lister-booking-direct-client')),
      LISTER_BOOKING_DIRECT_HOTEL: String(i18next.t('offer-history-template-lister-booking-direct-hotel')),
      LISTER_BOOKING_CANCELLATION_HOTEL: String(i18next.t('offer-history-template-lister-booking-cancellation-hotel')),
      LISTER_RESERVATION_HOTEL: String(i18next.t('offer-history-template-lister-reservation-hotel')),
      LISTER_RESERVATION_APPROVED_CLIENT: String(i18next.t('offer-history-template-lister-reservation-approved-client')),
      LISTER_BOOKING_REMINDER_START_SOON_HOTEL: String(i18next.t('offer-history-template-lister-booking-reminder-start-soon-hotel')),
      LISTER_EVENT_PLANNING_HOTEL: String(i18next.t('offer-history-template-lister-event-planning-hotel')),
      LISTER_MISSING_DATA_HOTEL: String(i18next.t('offer-history-template-lister-missing-data-hotel')),
      LISTER_MISSING_RESERVATION_CLIENT: String(i18next.t('offer-history-template-lister-missing-reservation-client')),
    };

    return templateDescriptions[templateName] || translateTemplateName(templateName);
  };

  // Function to get hotel name from email history
  const getHotelNameFromEmailHistory = (emailItem: EmailHistoryItem): string => {
    // Try to get hotel name from recipients or fallback to generic text
    const hotelRecipients = emailItem.recipients?.filter(
      r => r.source === 'HOTEL_BUSINESS_EMAIL' || r.source === 'HOTEL_NOTIFICATION_EMAIL' || r.source.startsWith('HOTEL_CONTACT_'),
    );

    if (hotelRecipients && hotelRecipients.length > 0) {
      return String(i18next.t('offer-history-email-hotel-with-email', { email: hotelRecipients[0].emailAddress }));
    }

    return String(i18next.t('hotel'));
  };

  // Function to open resend confirmation dialog
  const openResendConfirmDialog = (emailItem: EmailHistoryItem) => {
    const hotelName = getHotelNameFromEmailHistory(emailItem);
    const recipientInfo = formatPrimaryRecipients(emailItem.recipients);

    setResendConfirmDialog({
      open: true,
      emailHistoryId: emailItem.id,
      subject: emailItem.subject || String(i18next.t('offer-email-history-no-subject')),
      hotelName,
      recipientInfo,
    });
  };

  // Function to close resend confirmation dialog
  const closeResendConfirmDialog = () => {
    setResendConfirmDialog({
      open: false,
      emailHistoryId: null,
      subject: null,
      hotelName: null,
      recipientInfo: null,
    });
    // Reset CC emails state
    setShowCcInput(false);
    setCcEmails([]);
    setNewCcEmail('');
  };

  // Helper function to validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Add CC email
  const handleAddCcEmail = () => {
    const trimmedEmail = newCcEmail.trim();
    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      dispatch(
        enqueueSnackbar({
          message: String(i18next.t('offer-history-email-invalid-address')),
          options: { variant: 'error' },
        }),
      );
      return;
    }

    if (ccEmails.length >= 5) {
      dispatch(
        enqueueSnackbar({
          message: String(i18next.t('offer-history-email-max-cc')),
          options: { variant: 'error' },
        }),
      );
      return;
    }

    // Check for duplicates (case-insensitive)
    if (ccEmails.some(email => email.toLowerCase() === trimmedEmail.toLowerCase())) {
      dispatch(
        enqueueSnackbar({
          message: String(i18next.t('offer-history-email-duplicate-cc')),
          options: { variant: 'error' },
        }),
      );
      return;
    }

    setCcEmails([...ccEmails, trimmedEmail]);
    setNewCcEmail('');
  };

  // Remove CC email
  const handleRemoveCcEmail = (emailToRemove: string) => {
    setCcEmails(ccEmails.filter(email => email !== emailToRemove));
  };

  // Resend email function
  const handleResendEmail = async (emailHistoryId: number, subject: string | null | undefined) => {
    try {
      const { data } = await resendEmailMutation({
        variables: {
          emailHistoryId,
          ccEmails: ccEmails.length > 0 ? ccEmails : null,
        },
      });

      if (data?.resendEmailFromHistory?.success) {
        // Localized success message (server may still return its own message in payload)
        dispatch(
          enqueueSnackbar({
            message: i18next.t('offer-history-email-resend-success-generic', { subject: subject || i18next.t('email') }),
            options: { variant: 'success' },
          }),
        );
        // Close confirmation dialog and reset CC emails
        closeResendConfirmDialog();
      } else {
        // Show error message
        dispatch(
          enqueueSnackbar({
            message: i18next.t('offer-history-email-resend-error') + ': ' + (data?.resendEmailFromHistory?.message || i18next.t('offer-history-email-unknown-error')),
            options: { variant: 'error' },
          }),
        );
      }
      // Always refresh email history (even on error, as email history entry may have been created)
      if (onEmailResent) {
        onEmailResent();
      }
    } catch (error: any) {
      console.error('Error resending email:', error);
      dispatch(
        enqueueSnackbar({
          message: i18next.t('offer-history-email-resend-error') + ': ' + error.message,
          options: { variant: 'error' },
        }),
      );
      // Refresh email history even on error (email history entry may have been created with CC recipients)
      if (onEmailResent) {
        onEmailResent();
      }
    }
  };

  const getBookingHistoryData = () => {
    return _.orderBy(
      history.filter(h => h.status !== EOfferVersionHistoryStatus.PROCESS_FAILURE || user.isSeminargo || user.isRoot).map(h => ({
        createdAt: h.createdAt,
        status: i18next.t(`enums-EOfferVersionHistoryStatus-${h.status}`),
        username: h.userType === 'guest'
          ? i18next.t('offer-history-user-link-access')
          : (h.userType === 'customer' && !canSeeClientData) ? '**********' :
            h.publicClient ?
              h.user ? `${h.user.name} (${h.publicClient.firstname + ' ' + h.publicClient.lastname})` : h.publicClient.firstname + ' ' + h.publicClient.lastname :
              h.user ? h.user.name : '',
        usertype:
          h.userType === 'guest'
            ? i18next.t('offer-history-usertype-hotel')
            : h.userType === 'customer'
              ? h.user ? i18next.t('offer-history-usertype-sudo') : i18next.t('offer-history-usertype-customer')
              : h.userType === 'hotel'
                ? i18next.t('offer-history-usertype-hotel')
                : h.userType === 'seminargo'
                  ? i18next.t('offer-history-usertype-seminargo')
                  : i18next.t('offer-history-usertype-system'),
        action: h.extraInfo ? (() => {
          if (h.status === EOfferVersionHistoryStatus.RESPONSIBLE_PERSON_TRANSFERRED || h.status === EOfferVersionHistoryStatus.RESPONSIBLE_ACCOUNT_TRANSFERRED) {
            return <>{formatResponsiblePersonTransferredInfo(h.extraInfo)}</>;
          }
          try {
            const parsed = JSON.parse(h.extraInfo);
            // Suppress internal type markers; details are shown in the changes table.
            if (parsed.type === 'REQUEST_WITH_SERVICE_DETAILS' || parsed.type === 'REQUEST_WITHOUT_SERVICE_DETAILS') {
              return null;
            }
          } catch {
            // Not JSON, fall through to raw display.
          }
          return <>{h.extraInfo}</>;
        })() : h.offerVersion && onOfferVersionClick ? (
          <Button
            variant="text"
            onClick={() => {
              onOfferVersionClick(h.offerVersion!.id);
              closeHistoryDialog();
            }}
          >
            {i18next.t('offer-history-view')}
          </Button>
        ) : null,
        originalData: h,
        isProcessFailure: h.status === 'PROCESS_FAILURE',
      })),
      l => l.createdAt,
      'desc',
    );
  };

  const getEmailHistoryData = () => {
    return _.orderBy(
      [
        ...emailHistory
          .filter(e => e.success)
          .map(e => ({
            createdAt: e.createdAt,
            sender: canSeeClientData ? (e.userName || i18next.t('offer-history-email-system')) : '**********',
            primaryRecipients: canSeeClientData ? formatPrimaryRecipients(e.recipients) : '**********',
            alternativeRecipients: canSeeClientData ? formatAlternativeRecipients(e.recipients) : '**********',
            description: getEmailDescription(e.templateName),
            status: i18next.t('offer-history-email-sent'),
            deliveryStatus: getRecipientsDeliveryStatus(e.recipients),
            action: (
              canSeeClientData && <Box display="flex" gap={0.5}>
                <Tooltip title={i18next.t('offer-history-email-preview')}>
                  <IconButton
                    size="small"
                    onClick={event => {
                      event.stopPropagation(); // Prevent row click
                      const emailHistoryData = getEmailHistoryData();
                      const emailIndex = emailHistoryData.findIndex(item => item.originalData.id === e.id);
                      if (emailIndex !== -1) {
                        openHistoryDialog(emailIndex, true);
                      }
                    }}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={resendLoading ? i18next.t('offer-history-email-resending') : i18next.t('offer-history-email-resend')}>
                  <IconButton
                    size="small"
                    onClick={event => {
                      event.stopPropagation(); // Prevent row click
                      openResendConfirmDialog(e);
                    }}
                    disabled={resendLoading}
                    color="primary"
                  >
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
            originalData: e,
          })),
        ...emailHistory
          .filter(e => !e.success)
          .map(e => ({
            createdAt: e.createdAt,
            sender: canSeeClientData ? (e.userName || i18next.t('offer-history-email-system')) : '**********',
            primaryRecipients: canSeeClientData ? formatPrimaryRecipients(e.recipients) : '**********',
            alternativeRecipients: canSeeClientData ? formatAlternativeRecipients(e.recipients) : '**********',
            description: getEmailDescription(e.templateName),
            status: i18next.t('offer-history-email-failed'),
            deliveryStatus: getRecipientsDeliveryStatus(e.recipients),
            action: (
              canSeeClientData && <Box display="flex" gap={0.5}>
                <Tooltip title={i18next.t('offer-history-email-preview')}>
                  <IconButton
                    size="small"
                    onClick={event => {
                      event.stopPropagation(); // Prevent row click
                      const emailHistoryData = getEmailHistoryData();
                      const emailIndex = emailHistoryData.findIndex(item => item.originalData.id === e.id);
                      if (emailIndex !== -1) {
                        openHistoryDialog(emailIndex, true);
                      }
                    }}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={resendLoading ? i18next.t('offer-history-email-resending') : i18next.t('offer-history-email-resend')}>
                  <IconButton
                    size="small"
                    onClick={event => {
                      event.stopPropagation(); // Prevent row click
                      openResendConfirmDialog(e);
                    }}
                    disabled={resendLoading}
                    color="error"
                  >
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
            originalData: e,
          })),
      ],
      l => l.createdAt,
      'desc',
    );
  };

  const openHistoryDialog = (index: number, isEmailHistory: boolean = false) => {
    const data = isEmailHistory ? getEmailHistoryData() : getBookingHistoryData();
    setCurrentHistoryData(data);
    setCurrentHistoryIndex(index);
    setSelectedHistoryItem(data[index]);
  };

  const navigateHistory = (direction: 'prev' | 'next') => {
    if (currentHistoryData.length === 0) return;

    let newIndex = currentHistoryIndex;
    if (direction === 'prev' && currentHistoryIndex < currentHistoryData.length - 1) {
      newIndex = currentHistoryIndex + 1;
    } else if (direction === 'next' && currentHistoryIndex > 0) {
      newIndex = currentHistoryIndex - 1;
    }

    if (newIndex !== currentHistoryIndex) {
      setCurrentHistoryIndex(newIndex);
      setSelectedHistoryItem(currentHistoryData[newIndex]);
    }
  };

  const closeHistoryDialog = () => {
    setSelectedHistoryItem(null);
    setCurrentHistoryIndex(-1);
    setCurrentHistoryData([]);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <CustomTabsControlled
            selected={selectedHistoryTab}
            handleChange={selected => setSelectedHistoryTab(selected)}
            headers={[i18next.t('offer-history-booking-tab'), i18next.t('offer-history-email-tab')]}
            tabs={[
              <Grid container key="booking-history">
                <Grid item xs={12}>
                  <SimpleTable
                    headers={[
                      i18next.t('offer-versions-header-createdat'),
                      i18next.t('offer-versions-header-status'),
                      i18next.t('offer-versions-header-user'),
                      i18next.t('offer-versions-header-usertype'),
                    ]}
                    rows={getBookingHistoryData().map((row, index) => [
                      <FormatDateTime key={`date-${index}`} date={row.createdAt} />,
                      <span
                        key={`status-${index}`}
                        style={{ color: row.isProcessFailure ? '#d32f2f' : 'inherit', fontWeight: row.isProcessFailure ? 'bold' : 'normal' }}
                      >
                        {row.status || null}
                      </span>,
                      row.username || null,
                      row.usertype || null,
                    ])}
                    onRowClick={rowIndex => {
                      openHistoryDialog(rowIndex, false);
                    }}
                  />
                </Grid>
              </Grid>,
              <Grid container key="email-history">
                <Grid item xs={12}>
                  <SimpleTable
                    headers={[
                      i18next.t('offer-email-history-header-zeitpunkt'),
                      i18next.t('offer-email-history-header-sender'),
                      i18next.t('offer-email-history-header-recipients'),
                      i18next.t('offer-email-history-header-alternative-recipients'),
                      i18next.t('offer-email-history-header-description'),
                      i18next.t('offer-email-history-header-status'),
                      i18next.t('offer-email-history-header-actions'),
                    ]}
                    hidden={[
                      false,
                      !canSeeClientData,
                      !canSeeClientData,
                      !canSeeClientData,
                      false,
                      false,
                      !canSeeClientData,
                    ]}
                    rows={getEmailHistoryData().map((row, index) => [
                      <FormatDateTime key={`email-date-${index}`} date={row.createdAt} />,
                      <Typography key={`email-sender-${index}`} variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.sender}
                      </Typography>,
                      <Typography
                        key={`email-recipients-${index}`}
                        variant="body2"
                        sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={row.primaryRecipients}
                      >
                        {row.primaryRecipients}
                      </Typography>,
                      <Typography
                        key={`email-alt-recipients-${index}`}
                        variant="body2"
                        sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={row.alternativeRecipients}
                      >
                        {row.alternativeRecipients}
                      </Typography>,
                      <Typography
                        key={`email-description-${index}`}
                        variant="body2"
                        sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={row.description}
                      >
                        {row.description}
                      </Typography>,
                      <Box key={`email-status-${index}`} display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                        <Typography
                          variant="body2"
                          sx={{
                            color: row.originalData.success === true ? 'success.main' : row.originalData.success === false ? 'error.main' : 'inherit',
                            fontWeight: 'medium',
                          }}
                        >
                          {row.status}
                        </Typography>
                        {canSeeClientData && row.deliveryStatus && (
                          <Chip
                            label={translateDeliveryStatus(row.deliveryStatus)}
                            size="small"
                            color={deliveryStatusChipColor(row.deliveryStatus)}
                          />
                        )}
                      </Box>,
                      <Box key={`email-actions-${index}`} display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                        {row.action}
                        {canSeeClientData && row.originalData.err && (
                          <Chip label={i18next.t('offer-email-history-error-details')} size="small" color="error" variant="outlined" />
                        )}
                      </Box>,
                    ])}
                    onRowClick={canSeeClientData ? (rowIndex: number) => {
                      openHistoryDialog(rowIndex, true);
                    } : undefined}
                  />
                </Grid>
              </Grid>,
            ]}
          />
        </Grid>
      </Grid>

      <SimpleDialog
        open={!!selectedHistoryItem}
        setOpen={(open: boolean) => !open && closeHistoryDialog()}
        title={i18next.t('offer-history-details-title')}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '700px',
            maxHeight: '900px',
            width: '900px',
          },
        }}
        customActions={
          selectedHistoryItem && (
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  disabled={currentHistoryIndex >= currentHistoryData.length - 1}
                  onClick={() => navigateHistory('prev')}
                >
                  {i18next.t('offer-history-details-prev')}
                </Button>
                <Button variant="outlined" endIcon={<ArrowForwardIcon />} disabled={currentHistoryIndex <= 0} onClick={() => navigateHistory('next')}>
                  {i18next.t('offer-history-details-next')}
                </Button>
                <Typography variant="body2" color="textSecondary">
                  {currentHistoryIndex + 1} / {currentHistoryData.length}
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button variant="contained" onClick={closeHistoryDialog} color="primary">
                  {i18next.t('information-dialog-confirm')}
                </Button>
              </Box>
            </Box>
          )
        }
      >
        {selectedHistoryItem && (
          <Box sx={{ minHeight: '550px', maxHeight: '700px', overflowY: 'auto' }}>
            {selectedHistoryItem.originalData?.templateName && (
              <>
                <Grid item xs={12} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {i18next.t('offer-history-email-template-details')}
                  </Typography>
                  <Typography>
                    <strong>{i18next.t('offer-email-history-template')}:</strong>{' '}
                    {translateTemplateName(selectedHistoryItem.originalData.templateName)}
                  </Typography>
                  {selectedHistoryItem.originalData.language && (
                    <Typography>
                      <strong>{i18next.t('offer-email-history-language')}:</strong> {selectedHistoryItem.originalData.language}
                    </Typography>
                  )}
                  <Typography>
                    <strong>{i18next.t('offer-email-history-from')}:</strong> {selectedHistoryItem.originalData.fromAddress}
                  </Typography>
                  {selectedHistoryItem.originalData.replyToAddress && (
                    <Typography>
                      <strong>{i18next.t('offer-email-history-reply-to')}:</strong> {selectedHistoryItem.originalData.replyToAddress}
                    </Typography>
                  )}
                  <Typography>
                    <strong>{i18next.t('offer-email-history-user')}:</strong>{' '}
                    {selectedHistoryItem.originalData.userName || i18next.t('offer-history-email-system')}
                  </Typography>
                  {selectedHistoryItem.originalData.sentAt && (
                    <Typography>
                      <strong>{i18next.t('offer-email-history-sent-at')}:</strong> <FormatDateTime date={selectedHistoryItem.originalData.sentAt} />
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {i18next.t('offer-history-details-timestamp')}
                  </Typography>
                  <FormatDateTime date={selectedHistoryItem.createdAt} />
                </Grid>

                <Grid item xs={12} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {i18next.t('offer-history-details-status')}
                  </Typography>
                  <Typography>{selectedHistoryItem.status}</Typography>
                </Grid>

                <Grid item xs={12} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {i18next.t('offer-history-email-recipients')}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18next.t('offer-email-history-recipient-type')}</TableCell>
                          <TableCell>{i18next.t('offer-email-history-recipient-email')}</TableCell>
                          <TableCell>{i18next.t('offer-email-history-recipient-source')}</TableCell>
                          <TableCell>{i18next.t('offer-email-history-recipient-delivery-status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedHistoryItem.originalData.recipients && selectedHistoryItem.originalData.recipients.length > 0 ? (
                          selectedHistoryItem.originalData.recipients.map((recipient: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Chip label={recipient.recipientType} size="small" color={recipient.recipientType === 'TO' ? 'primary' : 'default'} />
                              </TableCell>
                              <TableCell>{recipient.emailAddress}</TableCell>
                              <TableCell>{translateSourceName(recipient.source)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={translateDeliveryStatus(recipient.deliveryStatus)}
                                  size="small"
                                  color={deliveryStatusChipColor(recipient.deliveryStatus)}
                                />
                                {recipient.deliveryStatus === 'ERROR' && (
                                  <Box sx={{ mt: 0.5 }}>
                                    {recipient.deliveryError && (
                                      <Typography variant="caption" color="error.main" display="block">
                                        {recipient.deliveryError}
                                      </Typography>
                                    )}
                                    {recipient.deliveryStatusAt && (
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        <FormatDateTime date={recipient.deliveryStatusAt} />
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell>
                              <Chip label="TO" size="small" color="primary" />
                            </TableCell>
                            <TableCell>{i18next.t('offer-email-history-unknown')}</TableCell>
                            <TableCell>{i18next.t('offer-email-history-no-enhanced-data')}</TableCell>
                            <TableCell>-</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {selectedHistoryItem.originalData.body && (
                  <Grid item xs={12} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {i18next.t('offer-history-email-body')}
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        p: 2,
                        maxHeight: 500,
                        overflowY: 'auto',
                        backgroundColor: '#f9f9f9',
                      }}
                      dangerouslySetInnerHTML={{ __html: selectedHistoryItem.originalData.body }}
                    />
                  </Grid>
                )}

                {selectedHistoryItem.originalData.attachments?.length > 0 && (
                  <Grid item xs={12} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {i18next.t('offer-history-email-attachments')}
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{i18next.t('offer-email-history-attachment-filename')}</TableCell>
                            <TableCell>{i18next.t('offer-email-history-attachment-type')}</TableCell>
                            <TableCell>{i18next.t('offer-email-history-attachment-reference')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedHistoryItem.originalData.attachments?.map((attachment: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>{attachment.filename}</TableCell>
                              <TableCell>
                                <Chip label={attachment.attachmentType} size="small" />
                              </TableCell>
                              <TableCell>
                                {attachment.offerVersionId && `${i18next.t('offer-email-history-offer-version')}: ${attachment.offerVersionId}`}
                                {attachment.eventId && `${i18next.t('offer-email-history-event')}: ${attachment.eventId}`}
                                {!attachment.offerVersionId && !attachment.eventId && i18next.t('offer-email-history-custom-file')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </>
            )}

            {/* Actions removed from dialog - only show for non-email history items */}
            {!selectedHistoryItem.originalData?.templateName && selectedHistoryItem.action && (
              <Grid item xs={12} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {i18next.t('offer-history-details-action')}
                </Typography>
                {selectedHistoryItem.action}
              </Grid>
            )}

            {/* Details gruppiert nach Tagen und Typen */}
            {(() => {
              // Sammle alle Details aus den verschiedenen Arrays mit Typ-Information
              const allDetails: Array<{
                id: number;
                changeDate: string;
                dayId?: number;
                type: 'service' | 'attendee' | 'planning' | 'billing';
                data:
                | WLOfferHistoryDetailServiceOutput
                | WLOfferHistoryDetailAttendeeOutput
                | WLOfferHistoryDetailPlanningOutput
                | WLOfferHistoryDetailBillingOutput;
              }> = [
                  ...(selectedHistoryItem.originalData?.serviceDetails || []).map((detail: WLOfferHistoryDetailServiceOutput) => ({
                    id: detail.id,
                    changeDate: detail.changeDate,
                    dayId: detail.dayId,
                    type: 'service' as const,
                    data: detail,
                  })),
                  ...(selectedHistoryItem.originalData?.attendeeDetails || []).map((detail: WLOfferHistoryDetailAttendeeOutput) => ({
                    id: detail.id,
                    changeDate: detail.changeDate,
                    dayId: detail.dayId,
                    type: 'attendee' as const,
                    data: detail,
                  })),
                  ...(selectedHistoryItem.originalData?.planningDetails || []).map((detail: WLOfferHistoryDetailPlanningOutput) => ({
                    id: detail.id,
                    changeDate: detail.changeDate,
                    dayId: detail.dayId,
                    type: 'planning' as const,
                    data: detail,
                  })),
                  ...(selectedHistoryItem.originalData?.billingDetails || []).map((detail: WLOfferHistoryDetailBillingOutput) => ({
                    id: detail.id,
                    changeDate: detail.changeDate,
                    dayId: detail.dayId,
                    type: 'billing' as const,
                    data: detail,
                  })),
                ];

              if (allDetails.length === 0) return null;

              // Separiere Allgemeine Details von Tag-spezifischen Details
              const generalDetails: typeof allDetails = [];
              const tagSpecificDetails: typeof allDetails = [];

              allDetails.forEach(detail => {
                // Prüfe explizit, ob dayId eine Zahl ist (auch 0 ist tag-spezifisch!)
                const isTagSpecific = typeof detail.dayId === 'number';

                if (isTagSpecific) {
                  tagSpecificDetails.push(detail);
                } else {
                  generalDetails.push(detail);
                }
              });

              // Gruppiere Tag-spezifische Details nach dayId
              const detailsByDay = tagSpecificDetails.reduce((acc: Record<number, typeof allDetails>, detail) => {
                const dayId = detail.dayId!;
                if (!acc[dayId]) {
                  acc[dayId] = [];
                }
                acc[dayId].push(detail);
                return acc;
              }, {});

              // Sortiere Tage
              const sortedDayIds = Object.keys(detailsByDay).map(Number).sort();

              // Berechne Startdatum vom offerVersion falls verfügbar
              const offerStartDate = selectedHistoryItem.originalData?.offerVersion?.startDate || selectedHistoryItem.offerVersion?.startDate;

              const getDetailInfo = (detail: (typeof allDetails)[0]) => {
                switch (detail.type) {
                  case 'service': {
                    const serviceDetail = detail.data as WLOfferHistoryDetailServiceOutput;
                    return {
                      displayName: serviceDetail.displayName,
                      prevValue: serviceDetail.previousCount?.toString() || '-',
                      newValue: serviceDetail.newCount?.toString() || '-',
                      changeValue:
                        serviceDetail.changeCount != null ? `${serviceDetail.changeCount > 0 ? '+' : ''}${serviceDetail.changeCount}` : '-',
                      changeType: serviceDetail.changeType,
                      extraInfo: `SERVICE${serviceDetail.sku ? ` | SKU: ${serviceDetail.sku}` : ''}`,
                    };
                  }
                  case 'attendee': {
                    const attendeeDetail = detail.data as WLOfferHistoryDetailAttendeeOutput;

                    // Translate enum values if they are language keys
                    const translateValueIfEnum = (value: string) => {
                      if (value && value.startsWith('enums-')) {
                        return i18next.t(value, { defaultValue: value });
                      }
                      return value;
                    };

                    return {
                      displayName: attendeeDetail.displayName,
                      prevValue: attendeeDetail.oldValue ? translateValueIfEnum(attendeeDetail.oldValue) : '-',
                      newValue: attendeeDetail.newValue ? translateValueIfEnum(attendeeDetail.newValue) : '-',
                      changeValue:
                        attendeeDetail.oldValue !== attendeeDetail.newValue
                          ? i18next.t('offer-history-changed')
                          : i18next.t('offer-history-unchanged'),
                      changeType: attendeeDetail.changeType,
                      extraInfo: 'ATTENDEE',
                    };
                  }
                  case 'planning': {
                    const planningDetail = detail.data as WLOfferHistoryDetailPlanningOutput;

                    // Translate enum values if they are language keys
                    const translateValueIfEnum = (value: string) => {
                      if (value && value.startsWith('enums-')) {
                        return i18next.t(value, { defaultValue: value });
                      }
                      return value;
                    };

                    return {
                      displayName: planningDetail.displayName,
                      prevValue: planningDetail.oldValue ? translateValueIfEnum(planningDetail.oldValue) : '-',
                      newValue: planningDetail.newValue ? translateValueIfEnum(planningDetail.newValue) : '-',
                      changeValue:
                        planningDetail.oldValue !== planningDetail.newValue
                          ? i18next.t('offer-history-changed')
                          : i18next.t('offer-history-unchanged'),
                      changeType: planningDetail.changeType,
                      extraInfo: planningDetail.changeCategory,
                    };
                  }
                  case 'billing': {
                    const billingDetail = detail.data as WLOfferHistoryDetailBillingOutput;

                    // Translate enum values if they are language keys
                    const translateValueIfEnum = (value: string) => {
                      if (value && value.startsWith('enums-')) {
                        return i18next.t(value, { defaultValue: value });
                      }
                      return value;
                    };

                    return {
                      displayName: billingDetail.displayName,
                      prevValue: billingDetail.oldValue ? translateValueIfEnum(billingDetail.oldValue) : '-',
                      newValue: billingDetail.newValue ? translateValueIfEnum(billingDetail.newValue) : '-',
                      changeValue:
                        billingDetail.oldValue !== billingDetail.newValue ? i18next.t('offer-history-changed') : i18next.t('offer-history-unchanged'),
                      changeType: billingDetail.changeType,
                      extraInfo: 'BILLING',
                    };
                  }
                }
              };

              return (
                <Grid item xs={12} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {i18next.t('offer-history-changes')} ({allDetails.length})
                  </Typography>

                  {/* Allgemeine Änderungen */}
                  {generalDetails.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                        {i18next.t('offer-history-general')}
                      </Typography>

                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{i18next.t('offer-history-entry')}</TableCell>
                              <TableCell align="center">{i18next.t('offer-history-before')}</TableCell>
                              <TableCell align="center">{i18next.t('offer-history-after')}</TableCell>
                              <TableCell>{i18next.t('offer-history-type')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {generalDetails
                              .sort((a, b) => a.id - b.id) // Sortiere nach ID für konsistente Reihenfolge
                              .map(detail => {
                                const info = getDetailInfo(detail);

                                return (
                                  <TableRow key={detail.id} hover>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight="medium">
                                        {info.displayName}
                                      </Typography>
                                      {info.extraInfo && (
                                        <Typography variant="caption" color="textSecondary">
                                          {info.extraInfo}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography variant="body2">{info.prevValue}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography variant="body2">{info.newValue}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={
                                          info.changeType === 'ADDED'
                                            ? i18next.t('offer-history-added')
                                            : info.changeType === 'REMOVED'
                                              ? i18next.t('offer-history-removed')
                                              : info.changeType === 'CHANGED'
                                                ? i18next.t('offer-history-changed')
                                                : i18next.t('offer-history-unchanged')
                                        }
                                        color={
                                          info.changeType === 'ADDED'
                                            ? 'success'
                                            : info.changeType === 'REMOVED'
                                              ? 'error'
                                              : info.changeType === 'CHANGED'
                                                ? 'primary'
                                                : 'default'
                                        }
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Tag-spezifische Änderungen */}
                  {sortedDayIds.map(dayId => {
                    const dayDetails = detailsByDay[dayId];

                    // Berechne das Datum für diesen Tag
                    const dayDate = offerStartDate ? moment(offerStartDate).add(dayId, 'days').toDate() : null;

                    return (
                      <Box key={dayId} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                          {i18next.t('offer-history-day')} {dayId + 1}
                          {dayDate && (
                            <>
                              {' '}
                              (<FormatDate date={dayDate} />)
                            </>
                          )}
                        </Typography>

                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{i18next.t('offer-history-entry')}</TableCell>
                                <TableCell align="center">{i18next.t('offer-history-before')}</TableCell>
                                <TableCell align="center">{i18next.t('offer-history-after')}</TableCell>
                                {dayDetails.some(detail => detail.type === 'service') && (
                                  <TableCell align="center">{i18next.t('offer-history-change')}</TableCell>
                                )}
                                <TableCell>{i18next.t('offer-history-type')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dayDetails
                                .sort((a, b) => a.id - b.id) // Sortiere nach ID für konsistente Reihenfolge
                                .map(detail => {
                                  const info = getDetailInfo(detail);

                                  return (
                                    <TableRow key={detail.id} hover>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                          {info.displayName}
                                        </Typography>
                                        {info.extraInfo && (
                                          <Typography variant="caption" color="textSecondary">
                                            {info.extraInfo}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="body2">{info.prevValue}</Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="body2">{info.newValue}</Typography>
                                      </TableCell>
                                      {dayDetails.some(detail => detail.type === 'service') && (
                                        <TableCell align="center">
                                          <Typography
                                            variant="body2"
                                            color={
                                              detail.type === 'service'
                                                ? (detail.data as WLOfferHistoryDetailServiceOutput).changeCount > 0
                                                  ? 'success.main'
                                                  : (detail.data as WLOfferHistoryDetailServiceOutput).changeCount < 0
                                                    ? 'error.main'
                                                    : 'textSecondary'
                                                : 'textSecondary'
                                            }
                                            fontWeight="medium"
                                          >
                                            {detail.type === 'service' ? info.changeValue : '-'}
                                          </Typography>
                                        </TableCell>
                                      )}
                                      <TableCell>
                                        <Chip
                                          label={
                                            info.changeType === 'ADDED'
                                              ? i18next.t('offer-history-added')
                                              : info.changeType === 'REMOVED'
                                                ? i18next.t('offer-history-removed')
                                                : info.changeType === 'CHANGED'
                                                  ? i18next.t('offer-history-changed')
                                                  : i18next.t('offer-history-unchanged')
                                          }
                                          color={
                                            info.changeType === 'ADDED'
                                              ? 'success'
                                              : info.changeType === 'REMOVED'
                                                ? 'error'
                                                : info.changeType === 'CHANGED'
                                                  ? 'primary'
                                                  : 'default'
                                          }
                                          size="small"
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}
                </Grid>
              );
            })()}

            {selectedHistoryItem.extraInfo && (
              <Grid item xs={12} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {i18next.t('offer-history-additional-info')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedHistoryItem.originalData?.status === EOfferVersionHistoryStatus.RESPONSIBLE_PERSON_TRANSFERRED
                    ? formatResponsiblePersonTransferredInfo(selectedHistoryItem.extraInfo)
                    : selectedHistoryItem.extraInfo}
                </Typography>
              </Grid>
            )}
          </Box>
        )}
      </SimpleDialog>

      {/* Resend Email Confirmation Dialog */}
      <SimpleDialog
        open={resendConfirmDialog.open}
        setOpen={(open: boolean) => !open && closeResendConfirmDialog()}
        title={i18next.t('offer-history-email-resend-confirm-title')}
        maxWidth="sm"
        fullWidth
        customActions={
          <Box display="flex" justifyContent="flex-end" gap={1} width="100%">
            <Button variant="outlined" onClick={closeResendConfirmDialog} disabled={resendLoading}>
              {i18next.t('offer-history-email-resend-cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (resendConfirmDialog.emailHistoryId) {
                  handleResendEmail(resendConfirmDialog.emailHistoryId, resendConfirmDialog.subject);
                }
              }}
              disabled={resendLoading}
              startIcon={resendLoading ? undefined : <EmailIcon />}
            >
              {resendLoading ? i18next.t('offer-history-email-resending') : i18next.t('offer-history-email-resend-confirm')}
            </Button>
          </Box>
        }
      >
        {resendConfirmDialog.open && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {i18next.t('offer-history-email-resend-confirm-message')}
            </Typography>

            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>{i18next.t('offer-history-email-resend-confirm-hotel')}:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {resendConfirmDialog.hotelName}
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>{i18next.t('offer-history-email-resend-confirm-subject')}:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {resendConfirmDialog.subject}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">
                  <strong>{i18next.t('offer-history-email-resend-confirm-recipients')}:</strong>
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowCcInput(!showCcInput)}
                  sx={{ color: '#AC2A6E' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="body2" sx={{ mb: showCcInput ? 2 : 0 }}>
                {resendConfirmDialog.recipientInfo}
              </Typography>

              {showCcInput && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    {i18next.t('offer-history-email-cc-title')}
                  </Typography>
                  <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={String(i18next.t('offer-history-email-cc-placeholder'))}
                        value={newCcEmail}
                        onChange={e => setNewCcEmail(e.target.value)}
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCcEmail();
                          }
                        }}
                        error={newCcEmail.trim() !== '' && !isValidEmail(newCcEmail.trim())}
                        helperText={
                          newCcEmail.trim() !== '' && !isValidEmail(newCcEmail.trim())
                            ? String(i18next.t('offer-history-email-invalid-address'))
                            : ccEmails.length >= 5
                              ? String(i18next.t('offer-history-email-max-addresses'))
                              : ''
                        }
                        disabled={ccEmails.length >= 5}
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddCcEmail}
                        disabled={
                          !newCcEmail.trim() ||
                          !isValidEmail(newCcEmail.trim()) ||
                          ccEmails.length >= 5 ||
                          ccEmails.some(email => email.toLowerCase() === newCcEmail.trim().toLowerCase())
                        }
                        sx={{ backgroundColor: '#AC2A6E', '&:hover': { backgroundColor: '#AC2A6E' } }}
                      >
                        <AddIcon fontSize="small" />
                      </Button>
                    </Grid>
                  </Grid>
                  {ccEmails.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {ccEmails.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={() => handleRemoveCcEmail(email)}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ borderColor: '#AC2A6E', color: '#AC2A6E' }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Typography variant="body2" color="textSecondary">
              {i18next.t('offer-history-email-resend-confirm-warning')}
            </Typography>
          </Box>
        )}
      </SimpleDialog>
    </>
  );
}
